import { useMemo } from "react";
import { useAccountsByEntity } from "@hooks/useAccounts";
import { useEntities } from "@hooks/useEntities";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { MatchableTransaction } from "../types";
import {
    buildTransactionLedgerContexts,
    getAccrualEntityIds,
    getGlEntityIds,
} from "./transactionLedgerContexts";

export function useMultiAllocationLedgerContext(
    transactions: MatchableTransaction[],
) {
    const entities = useEntities();
    const showAccountNumbers = useShowAccountNumbers();
    const entityIds = useMemo(
        () => getGlEntityIds({ transactions, entities }),
        [transactions, entities],
    );
    const {
        data: ledgersByEntityId,
        isLoading: isLoadingLedgers,
        isError,
    } = useLedgersByEntityIds(entityIds);
    const accrualEntityIds = useMemo(
        () => getAccrualEntityIds({ entityIds, ledgersByEntityId }),
        [entityIds, ledgersByEntityId],
    );
    const { accountsByEntityId, isLoading: isLoadingAccounts } =
        useAccountsByEntity(accrualEntityIds);
    const contextsByTransactionId = useMemo(
        () =>
            buildTransactionLedgerContexts({
                transactions,
                entities,
                ledgersByEntityId,
                accountsByEntityId,
            }),
        [transactions, entities, ledgersByEntityId, accountsByEntityId],
    );

    return {
        contextsByTransactionId,
        hasAnyAccrualLedger: accrualEntityIds.length > 0,
        showAccountNumbers,
        isLoading:
            (entityIds.length > 0 && isLoadingLedgers) || isLoadingAccounts,
        isError,
    };
}

export type MultiAllocationLedgerContext = ReturnType<
    typeof useMultiAllocationLedgerContext
>;
